// API Configuration for YouTube Optimizer
import { PlatformType } from '../types';

// The CRA dev server serves the bundle on :3000 while the API stays on :8080.
// Everywhere else one FastAPI process serves both, so the API is on the page's
// own origin. Hardcoding a hostname here breaks CORS: a page opened at
// 127.0.0.1:8080 calling localhost:8080 is cross-origin, and the preflight is
// rejected, so the DV360 partner fetch never runs.
const DEV_SERVER_PORT = '3000';
const BACKEND_PORT = '8080';

type ApiLocation = Pick<Location, 'origin' | 'hostname' | 'port' | 'protocol'>;

export const resolveApiBaseUrl = (
  location: ApiLocation | undefined,
  envOverride: string | undefined = process.env.REACT_APP_API_BASE_URL
): string => {
  if (envOverride) return envOverride;
  if (!location) return '';
  if (location.port === DEV_SERVER_PORT) {
    return `${location.protocol}//${location.hostname}:${BACKEND_PORT}`;
  }
  return location.origin;
};

export const ADK_API_BASE_URL = resolveApiBaseUrl(
  typeof window !== 'undefined' ? window.location : undefined
);

export const ADK_APP_NAME = 'youtube_optimizer_adk';

/**
 * Get the current user's session info from localStorage
 * @returns Object with userId and sessionId
 */
export const getSessionInfo = (): { userId: string; sessionId: string } => {
  let userId = localStorage.getItem('youtube_optimizer_user_id');
  let sessionId = localStorage.getItem('youtube_optimizer_session_id');

  // Generate new IDs if they don't exist
  if (!userId) {
    userId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('youtube_optimizer_user_id', userId);
  }

  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('youtube_optimizer_session_id', sessionId);
  }

  return { userId, sessionId };
};

/**
 * Reset session (creates new session ID, keeps user ID)
 */
export const resetSession = (): { userId: string; sessionId: string } => {
  const userId = localStorage.getItem('youtube_optimizer_user_id') ||
    `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  localStorage.setItem('youtube_optimizer_user_id', userId);
  localStorage.setItem('youtube_optimizer_session_id', sessionId);

  return { userId, sessionId };
};

/**
 * Get OAuth credentials from session storage
 * @returns OAuth credentials object or null
 */
export const getOAuthCredentials = (): any | null => {
  const credsJson = sessionStorage.getItem('oauth_credentials');
  if (!credsJson) return null;

  try {
    return JSON.parse(credsJson);
  } catch (error) {
    console.error('Failed to parse OAuth credentials:', error);
    return null;
  }
};

/**
 * Store OAuth credentials in session storage
 * @param credentials OAuth credentials object
 */
export const setOAuthCredentials = (credentials: any): void => {
  sessionStorage.setItem('oauth_credentials', JSON.stringify(credentials));
};

/**
 * Clear OAuth credentials
 */
export const clearOAuthCredentials = (): void => {
  sessionStorage.removeItem('oauth_credentials');
  sessionStorage.removeItem('user_email');
  sessionStorage.removeItem('platform_choice');
  sessionStorage.removeItem('selected_account');
};

/**
 * The message every authenticated call throws on a 401.
 *
 * Google access tokens live 3600s and nothing refreshes them, so a long
 * session always ends here. The app has a re-login path — the backend emits an
 * `auth_expired` SSE event on `invalid_grant` and AgentWorkspace turns that
 * into onLogout() — but the auth dependency rejects BEFORE the stream starts,
 * so there is no SSE body and that path cannot fire. Without this the user saw
 * "Failed to send message: Unauthorized" and had no way forward.
 */
export const AUTH_EXPIRED_MESSAGE = 'Session expired — sign in again';
export const SERVICE_BUSY_MESSAGE = 'The service is busy just now — please try again';

/**
 * Fallback message for a 410: the plan's session is gone (deleted by the
 * retention rule; session files hold Ads Planning API data). The backend's
 * own `detail` is shown when it sends one, so the period is said in one place.
 */
export const PLAN_EXPIRED_MESSAGE = 'This plan is no longer available. Start a new plan.';

const PLAN_GONE = 'PlanGoneError';

/** The error to throw for a 410 response, with the backend's wording. */
export const planGoneError = async (response: Response): Promise<Error> => {
  const body = await response.json().catch(() => ({}));
  const error = new Error(typeof body?.detail === 'string' && body.detail ? body.detail : PLAN_EXPIRED_MESSAGE);
  error.name = PLAN_GONE;
  return error;
};

/** Whether a caught error means the plan's session is gone (410). */
export const isPlanGoneError = (error: unknown): error is Error =>
  error instanceof Error && error.name === PLAN_GONE;

/** Whether a caught error means "the session expired", not "the call failed". */
export const isAuthExpiredError = (error: unknown): boolean =>
  error instanceof Error && error.message === AUTH_EXPIRED_MESSAGE;

/**
 * Attach the caller's credentials to a fetch.
 *
 * The backend now authenticates every session-scoped endpoint (ADR-009 C2):
 * ten of them previously accepted anonymous calls, so the whole pipeline could
 * be driven with no credentials. This sends the Google access token the app's
 * own OAuth flow already stores, which the backend verifies against Google's
 * tokeninfo rather than trusting.
 *
 * Existing headers are preserved — several callers set Content-Type, and
 * FormData uploads deliberately set none so the browser can add the boundary.
 */
export const withAuth = async (options: RequestInit = {}): Promise<RequestInit> => {
  const creds = getOAuthCredentials();
  if (!creds?.access_token) return options;
  return {
    ...options,
    headers: { ...(options.headers ?? {}), Authorization: `Bearer ${creds.access_token}` },
  };
};

/**
 * Download a file from a protected endpoint.
 *
 * `/download-file` requires a bearer token, and a plain `<a href>` navigation
 * cannot carry a header — the browser would be refused with a 401. So fetch it
 * with credentials and hand the bytes to the user as a blob instead.
 */
export const downloadProtectedFile = async (href: string, filename: string): Promise<void> => {
  const response = await fetch(href, await withAuth());
  if (!response.ok) {
    if (response.status === 410) throw await planGoneError(response);
    throw new Error(
      response.status === 401 ? AUTH_EXPIRED_MESSAGE :
      `Download failed: ${response.status}`
    );
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    // Revoking immediately can cancel the download in some browsers; give the
    // click a moment to start before releasing the object URL.
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  }
};


/**
 * Minimum shape this module needs from a response body.
 *
 * Declared structurally so a test can hand over a fake reader — jsdom does not
 * provide ReadableStream.
 */
export interface ByteStream {
  getReader(): {
    read(): Promise<{ done: boolean; value?: Uint8Array }>;
  };
}

/**
 * Read an SSE body and hand each complete event to `onEvent`.
 *
 * A frame is one line, and a line is NOT guaranteed to arrive in one
 * `read()` — the network splits where it likes. Parsing each chunk on its own
 * therefore drops any frame that straddles a boundary, and the bigger the
 * frame the likelier that is.
 *
 * That is not theoretical. The plan summary goes out as a single 2.4KB
 * `json:plan-summary` frame; it was split, both halves failed `JSON.parse`,
 * the catch swallowed them, and the panel showed only the small interstitial
 * that happened to arrive in a frame of its own. AgentWorkspace and the
 * audience step already buffered; the brief and summary steps did not.
 *
 * A parse failure is reported and skipped rather than thrown: one bad frame
 * must not abandon a stream the user is watching.
 */
export const readSSEStream = async (
  stream: ByteStream,
  onEvent: (event: any) => void
): Promise<void> => {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const handleLines = (lines: string[]) => {
    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      try {
        onEvent(JSON.parse(line.slice(6)));
      } catch (error) {
        console.error('Failed to parse SSE data:', error);
      }
    }
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    // The last piece may be half a line; hold it until more arrives.
    buffer = lines.pop() ?? '';
    handleLines(lines);
  }

  // A stream that ends without a trailing newline still owes us its last line.
  buffer += decoder.decode();
  if (buffer) handleLines(buffer.split('\n'));
};

/**
 * Create an ADK session for the user
 * @param appName The application name
 * @param userId The user ID
 * @param platform Optional platform type (google_ads or dv360)
 * @returns Promise that resolves to the session object with id
 */
export const createADKSession = async (
  appName: string,
  userId: string,
  platform?: PlatformType
): Promise<{id: string}> => {
  // Prepare initial state with platform info if provided
  const initialState: any = {};
  if (platform) {
    initialState.platform_choice = platform;
  }

  const response = await fetch(`${ADK_API_BASE_URL}/apps/${appName}/users/${userId}/sessions`, await withAuth({
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(initialState)
  }));

  if (response.status === 401) {
    throw new Error(AUTH_EXPIRED_MESSAGE);
  }
  if (!response.ok) {
    throw new Error(`Failed to create ADK session: ${response.statusText}`);
  }

  const session = await response.json();
  return session;
};

/**
 * Store the Step 1 configuration where the agents read it.
 * Throws AUTH_EXPIRED_MESSAGE on a 401 so the caller can send the user back
 * to sign in, and throws on any other failure rather than carrying on.
 */
export const saveSessionConfig = async (
  sessionId: string,
  config: Record<string, unknown>
): Promise<void> => {
  const response = await fetch(`${ADK_API_BASE_URL}/save-session-config`, await withAuth({
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ session_id: sessionId, config }),
  }));
  if (response.status === 401) {
    throw new Error(AUTH_EXPIRED_MESSAGE);
  }
  if (response.status === 410) {
    throw await planGoneError(response);
  }
  if (!response.ok) {
    throw new Error(`Failed to save session config: ${response.status}`);
  }
};

/**
 * Send a message to the ADK agent using streaming endpoint
 * @param appName The application name
 * @param userId The user ID
 * @param sessionId The session ID
 * @param message The message text
 * @param platform Optional platform type
 * @returns Promise that resolves to the ReadableStream
 */
export const sendADKMessageStreaming = async (
  appName: string,
  userId: string,
  sessionId: string,
  message: string,
  platform?: PlatformType
): Promise<ReadableStream> => {
  // Prepare state delta with platform info if provided
  const stateDelta: any = {};
  if (platform) {
    stateDelta.platform_choice = platform;
  }

  const response = await fetch(`${ADK_API_BASE_URL}/run_sse`, await withAuth({
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      appName,
      userId,
      sessionId,
      newMessage: {
        content: message
      },
      streaming: true,
      ...(Object.keys(stateDelta).length > 0 ? { stateDelta } : {})
    })
  }));

  if (response.status === 401) {
    throw new Error(AUTH_EXPIRED_MESSAGE);
  }
  if (response.status === 410) {
    throw await planGoneError(response);
  }
  if (response.status === 503) {
    // A retry, not a sign-out: the sign-in check could not reach Google,
    // session storage did not answer, or Cloud Run is busy. The server's own
    // detail says which, when there is one.
    const body = await response.json().catch(() => ({}));
    throw new Error(typeof body?.detail === 'string' && body.detail ? body.detail : SERVICE_BUSY_MESSAGE);
  }
  if (!response.ok) {
    throw new Error(`Failed to send message: ${response.statusText || response.status}`);
  }

  if (!response.body) {
    throw new Error('Response body is null');
  }

  return response.body;
};

/**
 * Fetch visualization data for a session
 * @param appName The application name
 * @param userId The user ID
 * @param sessionId The session ID
 * @returns Promise that resolves to the visualization data
 */
export const fetchVisualizationData = async (
  appName: string,
  userId: string,
  sessionId: string,
  requestId?: string
): Promise<any[]> => {
  const url = requestId
    ? `${ADK_API_BASE_URL}/visualization/${appName}/${userId}/${sessionId}?request_id=${encodeURIComponent(requestId)}`
    : `${ADK_API_BASE_URL}/visualization/${appName}/${userId}/${sessionId}`;

  const response = await fetch(url, {
    method: 'GET'
  });

  if (!response.ok) {
    console.warn('Failed to fetch visualization data:', response.statusText);
    return [];
  }

  const data = await response.json();
  return data.visualization_data || [];
};

export { ADK_API_BASE_URL as API_BASE_URL };
